import React, { useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from "@stripe/react-stripe-js";
import { AuthServices } from "../services/AuthServices";

const stripePromise = loadStripe(
  process.env.REACT_APP_TEST_STRIPE_PUBLISH_KEY ||
    process.env.REACT_APP_STRIPE_PUBLISH_KEY,
);

const Subscriptions = () => {
  const [errorMessage, setErrorMessage] = useState("");
  const [loadingTier, setLoadingTier] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [billingCycle, setBillingCycle] = useState("monthly");

  const handleSubmit = async (e) => {
    e.preventDefault();
    const tier = e.currentTarget.name;

    setErrorMessage("");
    setLoadingTier(tier);

    try {
      const response = await AuthServices.createSubscription(tier);

      if (!response?.success || !response?.data?.clientSecret) {
        throw new Error("Failed to create subscription session");
      }

      setClientSecret(response.data.clientSecret);
    } catch (error) {
      console.error(error);
      setErrorMessage(
        "There was an error starting checkout. Please try again.",
      );
    } finally {
      setLoadingTier(null);
    }
  };

  if (clientSecret) {
    return (
      <div className="checkout-container">
        <EmbeddedCheckoutProvider
          stripe={stripePromise}
          options={{ clientSecret }}
        >
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      </div>
    );
  }

  return (
    <div className="partner-tier-container">
      <div className="billing-cycle-container">
        <button
          className={`billing-btn ${billingCycle === "monthly" ? "active" : ""}`}
          onClick={() => setBillingCycle("monthly")}
        >
          Monthly
        </button>
        <button
          className={`billing-btn ${billingCycle === "yearly" ? "active" : ""}`}
          onClick={() => setBillingCycle("yearly")}
        >
          Yearly
        </button>
      </div>

      <div className="partner-tiers">
        {billingCycle === "monthly" ? (
          <div className="tier-container">
            <div className="tier basic-tier">
              <span className="tier-badge">For new businesses</span>
              <h3>Starter</h3>
              <p className="tier-subtitle">
                Get online fast and start taking orders
              </p>

              <div className="price">
                <span className="amount">$39.99</span>
                <span className="period">/month</span>
              </div>

              <ul>
                <li>✔ Up to 2 stores</li>
                <li>✔ 100 product listings</li>
                <li>✔ 5-mile delivery radius</li>
                <li>✔ Same-day delivery access</li>
                <li>✔ Priority customer support</li>
              </ul>

              <button
                onClick={handleSubmit}
                name="starter"
                className="choose-button"
                disabled={loadingTier === "starter"}
              >
                {loadingTier === "starter"
                  ? "Loading..."
                  : "Start with Starter"}
              </button>
            </div>

            <div className="tier growth-tier featured">
              <span className="tier-badge popular">Most Popular</span>
              <h3>Growth</h3>
              <p className="tier-subtitle">
                Scale faster with unlimited listings
              </p>

              <div className="price">
                <span className="amount">$79.99</span>
                <span className="period">/month</span>
              </div>

              <ul>
                <li>✔ Up to 2 stores</li>
                <li>✔ Unlimited product listings</li>
                <li>✔ 10-mile delivery radius</li>
                <li>✔ Advanced heatmap analytics</li>
                <li>✔ Priority chat support</li>
              </ul>

              <button
                onClick={handleSubmit}
                name="growth"
                className="choose-button primary"
                disabled={loadingTier === "growth"}
              >
                {loadingTier === "growth" ? "Loading..." : "Upgrade to Growth"}
              </button>
            </div>

            <div className="tier pro-tier">
              <span className="tier-badge">For high-volume brands</span>
              <h3>Pro</h3>
              <p className="tier-subtitle">Maximum reach, data, and support</p>

              <div className="price">
                <span className="amount">$149.99</span>
                <span className="period">/month</span>
              </div>

              <ul>
                <li>✔ Up to 2 stores</li>
                <li>✔ Unlimited product listings</li>
                <li>✔ 15+ mile delivery radius</li>
                <li>✔ Customer data export</li>
                <li>✔ Dedicated 24/7 account lead</li>
              </ul>

              <button
                onClick={handleSubmit}
                name="pro"
                className="choose-button"
                disabled={loadingTier === "pro"}
              >
                {loadingTier === "pro" ? "Loading..." : "Go Pro"}
              </button>
            </div>
          </div>
        ) : (
          <div className="tier-container">
            <div className="tier basic-tier">
              <span className="tier-badge">For new businesses</span>
              <h3>Starter Annual</h3>
              <p className="tier-subtitle">
                Get online fast and start taking orders
              </p>

              <div className="price">
                <span className="amount">$399.99</span>
                <span className="period">/Year</span>
              </div>

              <ul>
                <li>✔ Up to 2 stores</li>
                <li>✔ 100 product listings</li>
                <li>✔ 5-mile delivery radius</li>
                <li>✔ Same-day delivery access</li>
                <li>✔ Priority customer support</li>
              </ul>

              <button
                onClick={handleSubmit}
                name="starter_annual"
                className="choose-button"
                disabled={loadingTier === "starter"}
              >
                {loadingTier === "starter"
                  ? "Loading..."
                  : "Start with Starter"}
              </button>
            </div>

            <div className="tier growth-tier featured">
              <span className="tier-badge popular">Most Popular</span>
              <h3>Growth</h3>
              <p className="tier-subtitle">
                Scale faster with unlimited listings
              </p>

              <div className="price">
                <span className="amount">$799.99</span>
                <span className="period">/Year</span>
              </div>

              <ul>
                <li>✔ Up to 2 stores</li>
                <li>✔ Unlimited product listings</li>
                <li>✔ 10-mile delivery radius</li>
                <li>✔ Advanced heatmap analytics</li>
                <li>✔ Priority chat support</li>
              </ul>

              <button
                onClick={handleSubmit}
                name="growth_annual"
                className="choose-button primary"
                disabled={loadingTier === "growth"}
              >
                {loadingTier === "growth" ? "Loading..." : "Upgrade to Growth"}
              </button>
            </div>

            <div className="tier pro-tier">
              <span className="tier-badge">For high-volume brands</span>
              <h3>Pro</h3>
              <p className="tier-subtitle">Maximum reach, data, and support</p>

              <div className="price">
                <span className="amount">$1499.99</span>
                <span className="period">/Year</span>
              </div>

              <ul>
                <li>✔ Up to 2 stores</li>
                <li>✔ Unlimited product listings</li>
                <li>✔ 15+ mile delivery radius</li>
                <li>✔ Customer data export</li>
                <li>✔ Dedicated 24/7 account lead</li>
              </ul>

              <button
                onClick={handleSubmit}
                name="pro_annual"
                className="choose-button"
                disabled={loadingTier === "pro"}
              >
                {loadingTier === "pro" ? "Loading..." : "Go Pro"}
              </button>
            </div>
          </div>
        )}

        {errorMessage && <p className="error-message">{errorMessage}</p>}
      </div>
    </div>
  );
};

export default Subscriptions;
